import React, { useState } from 'react';
import { Snackbar, Alert } from '@mui/material';

// Read the logged-in user's JWT the same way the axios interceptors do.
const readToken = () => {
  try {
    const data = JSON.parse(localStorage.getItem('authData') || 'null');
    return data?.admin?.Token || data?.token || null;
  } catch {
    return null;
  }
};

// Append the token as a query param so a plain browser navigation (which can't
// set an Authorization header) still authenticates against JWTAuthMiddleware.
const withToken = (url) => {
  const token = readToken();
  if (!url || !token) return url || '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};

const messageFor = (status) => {
  if (status === 401) return 'Your session has expired — please refresh and sign in again.';
  if (status === 403) return "Access denied — you don't have permission to view this proof.";
  if (status === 404) return 'Proof document not found. Please ask the employee to re-upload it.';
  if (status === 503) return 'Document storage is temporarily unavailable — please try again in a moment.';
  return 'Unable to load the proof. Please try again.';
};

/**
 * "View proof" link that opens an uploaded declaration proof.
 *
 * The proof endpoint requires auth and 302-redirects to the file. A plain
 * <a target="_blank"> would, on failure, dump the backend's raw JSON error into
 * a blank tab. Instead we first make an authenticated fetch: on success we hand
 * the (now-authorized) URL off to a normal browser navigation so the file renders
 * natively; on 401/403/404/network error we close the tab and show a styled
 * in-app message.
 */
export default function ProofLink({ proof, label = 'View proof', title, style }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const rawUrl = proof?.download_url || proof?.file || '';

  const open = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!rawUrl) {
      setErr('No document is attached to this proof.');
      return;
    }
    // Open the tab synchronously, inside the click gesture, so it isn't blocked
    // by the pop-up blocker after the awaited fetch.
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const token = readToken();
      const res = await fetch(rawUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        redirect: 'manual', // success = a 302 to the file -> shows up as opaqueredirect
      });
      const ok = res.type === 'opaqueredirect' || (res.status >= 200 && res.status < 400);
      if (ok) {
        if (win) win.location = withToken(rawUrl);
        else setErr('Please allow pop-ups to view the proof.');
      } else {
        if (win) win.close();
        setErr(messageFor(res.status));
      }
    } catch {
      if (win) win.close();
      setErr('Unable to load the proof. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <a
        href={withToken(rawUrl)}
        onClick={open}
        target="_blank"
        rel="noreferrer"
        title={title}
        style={{ cursor: 'pointer', ...(style || {}) }}
      >
        {label}
      </a>
      <Snackbar
        open={!!err}
        autoHideDuration={6000}
        onClose={() => setErr('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErr('')} sx={{ fontWeight: 600 }}>
          {err}
        </Alert>
      </Snackbar>
    </>
  );
}
